<template>
    <DCard padding>
        <DRow>
            <DCol>
                <DAlert
                    v-if="manualInvoiceUpload.status === 'open'"
                    color="warning"
                    class="d-flex align-items-center"
                >
                    <DIcon
                        name="alert"
                        :size="24"
                    />
                    <div class="pr-2">
                        {{ __('tools.invoice_uploads.show.info_box') }}
                    </div>
                </DAlert>
                <div class="table-responsive">
                    <DTable
                        :items="itemsData"
                        :fields="filteredFields"
                        :sorter="isSortable()"
                        :sorter-value="sorterValue"
                        @update:sorter-value="updateOrderValue"
                    >
                        <template #actions="{item}">
                            <td v-if="props.manualInvoiceUpload.status === 'open'">
                                <DFormCheck
                                    v-if="item.id"
                                    :id="`invoice_item_${item.id}`"
                                    :custom="true"
                                    :checked="form.invoiceItemIds.includes(item.id) || item.error?.length > 0 || item.status === 'done'"
                                    :disabled="item.error?.length > 0 || item.status === 'done'"
                                    @click="updateCheckedItems(item)"
                                />
                            </td>
                        </template>
                        <template #contract="{item}">
                            <td v-if="userPermissions.includes('contract.view') && item.contract && item.customer && item.statusContract">
                                <Link :href="contractRoute(item)">
                                    {{ item.contract }}
                                </Link>
                            </td>
                            <td v-else>
                                {{ item.contract }}
                            </td>
                        </template>
                        <template #customer="{item}">
                            <td v-if="userPermissions.includes('customer.view') && item.customer && item.statusCustomer">
                                <Link :href="customerProfileRoute(item.customer)">
                                    {{ item.customer }}
                                </Link>
                            </td>
                            <td v-else>
                                {{ item.customer }}
                            </td>
                        </template>
                        <template #invoice="{item}">
                            <td v-if="userPermissions.includes('invoice.view') && item.invoice && item.customer">
                                <Link :href="invoiceRoute(item)">
                                    {{ item.invoice }}
                                </Link>
                            </td>
                            <td v-else>
                                {{ item.invoice }}
                            </td>
                        </template>
                        <template #error="{item}">
                            <td>
                                <div
                                    v-for="error in getErrors(item?.error)"
                                    :key="error"
                                >
                                    {{ error }}
                                </div>
                            </td>
                        </template>
                        <template #statusManualInvoice="{item}">
                            <td>
                                <DTag
                                    :label="getStatusTagLabel(item)"
                                    :color="getStatusTagColor(item)"
                                />
                            </td>
                        </template>
                    </DTable>
                </div>
                <DPagination
                    v-if="pagination.last_page > 1"
                    :active-page="pagination.current_page"
                    :pages="pagination.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DModal
            v-if="showDeleteConfirmationModal"
            v-model:show="showDeleteConfirmationModal"
            :title="__('tools.invoice_uploads.cancel.modal_title')"
            :message="__('tools.invoice_uploads.cancel.modal_text')"
            is-confirm
            is-error
            @close="closeCancellationModal"
            @confirm="cancelUpload"
        />
        <DFabs>
            <DFab
                v-if="manualInvoiceUpload.status === 'open'"
                id="btn-submit-invoices"
                icon="eye"
                color="primary"
                :label="__('tools.invoice_uploads.show.submit')"
                @click="previewInvoices"
            />
            <DFab
                v-if="manualInvoiceUpload.status === 'open'"
                id="btn-show-cancel-invoices"
                icon="close"
                color="danger"
                :label="__('tools.invoice_uploads.show.cancel')"
                @click="cancelInvoices"
            />
            <DFab
                id="btn-preview-invoice-upload-index"
                icon="close"
                color="secondary"
                @click="manualInvoiceUploadRoute"
            />
        </DFabs>
    </DCard>
    <DCard
        v-if="errors"
        :title="__('tools.invoice_uploads.upload.file_errors')"
        padding
        class="mt-3"
    >
        <DRow>
            <DFormRow v-if="errors">
                {{ errors }}
            </DFormRow>
        </DRow>
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { router, usePage, useForm } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, ref } from 'vue'

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    paginationData: {
        type: Object,
        default: () => {
            return {}
        }
    },
    manualInvoiceUpload: {
        type: Object,
        default: () => {
            return {}
        }
    },
    orderValues: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const fields = [
    { key: 'id', sorter: true, label: __('tools.invoice_uploads.show.id'), _style: { minWidth: '10%' } },
    { key: 'name', sorter: false, label: __('tools.invoice_uploads.show.name') },
    { key: 'description', sorter: false, label: __('tools.invoice_uploads.show.description') },
    { key: 'quantity', sorter: true, label: __('tools.invoice_uploads.show.quantity') },
    { key: 'price', sorter: true, label: __('tools.invoice_uploads.show.price') },
    { key: 'customer', sorter: true, label: __('tools.invoice_uploads.show.customer') },
    { key: 'contract', sorter: true, label: __('tools.invoice_uploads.show.contract') },
    { key: 'invoice', sorter: false, label: __('tools.invoice_uploads.show.invoice') },
    { key: 'statusContract', sorter: false, label: __('tools.invoice_uploads.show.statusContract') },
    { key: 'statusCustomer', sorter: false, label: __('tools.invoice_uploads.show.statusCustomer') },
    { key: 'error', sorter: true, label: __('tools.invoice_uploads.show.error') },
    { key: 'statusManualInvoice', sorter: true, label: __('tools.invoice_uploads.show.statusManualInvoice') }
]

const filteredFields = [
    ...(props.manualInvoiceUpload.status === 'open' ? [{ key: 'actions', sorter: false, label: __('tools.invoice_uploads.show.excluded_invoices'), _style: { minWidth: '5%' } }] : []),
    ...fields
]

const pagination = ref(props.paginationData)
const itemsData = ref(props.items)
const errors = ref(null)
const orderBy = ref(props.orderValues.orderBy ?? 'id')
const orderByAsc = ref(props.orderValues.orderByAsc ?? true)

const form = useForm({
    invoiceItemIds: itemsData.value.filter((item) => item.checked).map((item) => item.id)
})

// Here we save the list of already visited pages. We need to do that so that we don't
// update the list of excluded invoice items from BE multiple times for the same page
const excludedItemsByPageTracker = ref([1])

const userPermissions = computed(() => usePage().props.auth.permissions)

const manualInvoiceUploadRoute = () => router.get(route('tools.manual-invoice-uploads.index'))

const customerProfileRoute = (id) => route('customers.customers.show', id)

const invoiceRoute = (item) => route('customers.customers.invoices.show', { customer: item.customer, invoice: item.invoice })

const contractRoute = (value) => {
    return route('customers.customers.contracts.show', {
        customer: value.customer,
        contract: value.contract
    })
}
const updateCheckedItems = (invoiceItem) => {
    const itemIndex = form.invoiceItemIds.indexOf(invoiceItem.id)
    if (itemIndex < 0) {
        form.invoiceItemIds.push(invoiceItem.id)
        return
    }
    form.invoiceItemIds.splice(itemIndex, 1)
}

const previewInvoices = async () => {
    try {
        const params = {
            excludedInvoices: form.invoiceItemIds,
            manualInvoiceUpload: props.manualInvoiceUpload.id
        }

        const response = await window.axios.post(route('tools.manual-invoice-uploads.excluded', params))

        if (response.status === 200) {
            router.get(route('tools.manual-invoice-uploads.preview', { manualInvoiceUpload: props.manualInvoiceUpload.id }))
        }
    } catch (error) {
        errors.value = error.response.data.error
    }
}

const getStatusTagColor = (value) => {
    if (value.error.length > 0) {
        return 'danger'
    }
    if (value.invoice !== '') {
        return 'success'
    }
    if (value.checked === 1) {
        return 'danger'
    }
    if (value.status === 'open') {
        return 'primary'
    }
    if (value.status === 'done') {
        return 'success'
    }
    if (value.status === 'pending') {
        return 'secondary'
    }
    return 'danger'
}

const getStatusTagLabel = (value) => {
    if (value.error.length > 0) {
        return __('tools.invoice_uploads.statuses.error')
    }
    if (value.invoice !== '') {
        return __('tools.invoice_uploads.statuses.invoiced')
    }
    if (value.checked === true) {
        return __('tools.invoice_uploads.statuses.excluded')
    }
    if (value.status === 'done') {
        return __('tools.invoice_uploads.statuses.done')
    }
    if (value.status === 'pending') {
        return __('tools.invoice_uploads.statuses.pending')
    }
    return __('tools.invoice_uploads.statuses.open')
}

// Cancel upload
const showDeleteConfirmationModal = ref(false)
const closeCancellationModal = () => {
    showDeleteConfirmationModal.value = false
}
const cancelUpload = () => {
    router.post(route('tools.manual-invoice-uploads.cancel', {
        manualInvoiceUpload: props.manualInvoiceUpload.id
    }))
}
const cancelInvoices = () => {
    showDeleteConfirmationModal.value = true
}

const getErrors = (error) => {
    if (error) {
        const errors = error.split(',')
        return errors.map(er => __(`tools.invoice_uploads.errors.${er}`))
    }
    return []
}

const isSortable = () => {
    if (!sorterValue.value.column) return false

    return { external: true }
}

const sorterValue = computed(() => {
    return {
        column: orderBy.value,
        asc: !!orderByAsc.value
    }
})
const updateOrderValue = (event) => {
    orderByAsc.value = event.asc ? 1 : 0
    orderBy.value = event.column
    changePage(pagination.value.current_page)
}

const changePage = async (page) => {
    const params = {
        manualInvoiceUpload: props.manualInvoiceUpload.id,
        page: page,
        orderBy: orderBy.value,
        orderByAsc: +orderByAsc.value,
        front: true
    }

    const response = await window.axios.get(route('tools.manual-invoice-uploads.show', params))
    pagination.value = response.data
    itemsData.value = response.data.items
    orderByAsc.value = response.data.orderValues.orderByAsc
    orderBy.value = response.data.orderValues.orderBy

    // If we haven't visited the current page yet
    if (!(page in excludedItemsByPageTracker.value)) {
        excludedItemsByPageTracker.value.push(page)

        // Then merge the list of excluded items from BE response
        form.invoiceItemIds = [...new Set([
            ...form.invoiceItemIds,
            ...response.data.items.filter((item) => item.checked).map((item) => item.id)
        ])]
    }
}

</script>

<style scoped lang="scss">

</style>
